import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import React, { forwardRef, useEffect } from "react";
import { BaseChartColors } from "../constants";
import { configureNumberFormatter, configurePadding } from "../utils";
import { ClusteredColumnChartDataItem, ProcessedChartData } from "./types";
import {
  createLegends,
  createXAxis,
  createYAxis,
  tooltipTextProducer,
} from "./utils";
interface ClusteredColumnChartProps {
  id: string;
  chartData: ClusteredColumnChartDataItem[];
  baseColor?: BaseChartColors | string[];
  yAxisLabel?: string;
  showPercentageSymbol?: boolean;
  cursorBehavior?: "zoomXY";
  showCustomTooltip?: boolean;
}

export const ClusteredColumnChart = forwardRef<
  am4charts.XYChart,
  ClusteredColumnChartProps
>(
  (
    {
      id,
      baseColor = BaseChartColors.Blue,
      chartData: data,
      yAxisLabel,
      showPercentageSymbol,
      cursorBehavior = "zoomXY",
      showCustomTooltip = false,
    },
    ref
  ) => {
    useEffect(() => {
      const chart = am4core.create(id, am4charts.XYChart);
      if (ref) {
        (ref as React.MutableRefObject<am4charts.XYChart>).current = chart;
      }
      configurePadding(chart);
      configureNumberFormatter(chart);

      const processedData = data.map((item) => {
        const result: ProcessedChartData = {
          category: item.category,
          totalCompanyCount: item.totalCompanyCount || 0,
        };
        for (const series of item.cluster) {
          result[series.name] = series.value;
          result[`${series.name}_count`] = series.companyCount || 0;
        }
        return result;
      });

      chart.data = processedData;

      createLegends(chart);

      const seriesNames = Array.from(
        new Set(
          data.flatMap((item) => item.cluster.map((series) => series.name))
        )
      ).sort((a, b) => a.localeCompare(b));

      const xAxis = createXAxis(chart);
      createYAxis(chart, showPercentageSymbol, yAxisLabel);

      function createSeries(
        _value: ClusteredColumnChartDataItem,
        name: string,
        index: number
      ): am4charts.ColumnSeries {
        const series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = name;
        series.dataFields.categoryX = "category";
        series.name = name;

        if (Array.isArray(baseColor)) {
          const color = baseColor[index % baseColor.length];
          series.columns.template.fill = am4core.color(color);
          series.columns.template.stroke = am4core.color(color);
        } else {
          const baseColorObj = am4core.color(baseColor);
          const lightenFactor = 0.15 + 0.08 * index;
          series.columns.template.fill = baseColorObj.lighten(lightenFactor);
          series.columns.template.stroke = baseColorObj.lighten(lightenFactor);
        }
        const seriesColumn =
          cursorBehavior === "zoomXY" ? series : series.columns.template;

        if (showCustomTooltip) {
          tooltipTextProducer(series, name);
        } else {
          seriesColumn.tooltipText = `Product Name: {name}[/]\n${yAxisLabel}: {valueY}`;
        }

        seriesColumn.tooltipPosition = "pointer";
        series.columns.template.cursorOverStyle =
          am4core.MouseCursorStyle.pointer;

        const hoverState = series.columns.template.states.create("hover");
        hoverState.properties.fillOpacity = 0.8;

        series.events.on("hidden", arrangeColumns);
        series.events.on("shown", arrangeColumns);

        return series;
      }

      let index = 0;
      for (const name of seriesNames) {
        createSeries(data[index], name, index);
        index++;
      }

      function arrangeColumns() {
        const series = chart.series.getIndex(0);

        if (!series) {
          return;
        }

        const availableColumnWidth =
          1 -
          xAxis.renderer.cellStartLocation -
          (1 - xAxis.renderer.cellEndLocation);
        if (series.dataItems.length > 1) {
          const firstDataItem = series.dataItems.getIndex(0);
          const secondDataItem = series.dataItems.getIndex(1);

          if (!firstDataItem || !secondDataItem) {
            return;
          }

          const x0 = xAxis.getX(firstDataItem, "categoryX");
          const x1 = xAxis.getX(secondDataItem, "categoryX");
          const delta =
            ((x1 - x0) / chart.series.length) * availableColumnWidth;
          if (am4core.isNumber(delta)) {
            const middle = chart.series.length / 2;

            let newIndex = 0;
            chart.series.each(function (series) {
              if (!series.isHidden && !series.isHiding) {
                series.dummyData = newIndex;
                newIndex++;
              } else {
                series.dummyData = chart.series.indexOf(series);
              }
            });
            const visibleCount = newIndex;
            const newMiddle = visibleCount / 2;

            chart.series.each(function (series) {
              const trueIndex = chart.series.indexOf(series);
              const newIndex = series.dummyData;

              const dx = (newIndex - trueIndex + middle - newMiddle) * delta;

              series.animate(
                { property: "dx", to: dx },
                series.interpolationDuration,
                series.interpolationEasing
              );
              series.bulletsContainer.animate(
                { property: "dx", to: dx },
                series.interpolationDuration,
                series.interpolationEasing
              );
            });
          }
        }
      }

      const cursor = new am4charts.XYCursor();
      cursor.behavior = cursorBehavior;
      cursor.showTooltipOn = "hover";
      cursor.lineX.stroke = am4core.color("#000");
      cursor.lineY.stroke = am4core.color("#000");
      cursor.lineX.disabled = true;
      cursor.lineY.disabled = true;
      chart.cursor = cursor;

      return () => {
        chart.dispose();
      };
    }, [
      id,
      baseColor,
      data,
      showPercentageSymbol,
      yAxisLabel,
      cursorBehavior,
      showCustomTooltip,
      ref,
    ]);

    return <div id={id} style={{ width: "100%", height: "100%" }}></div>;
  }
);
